import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip,
  CircularProgress, Divider, Table, TableBody, TableCell, TableRow
} from '@mui/material';
import { ArrowBack, Edit, DirectionsCar } from '@mui/icons-material';
import { productsAPI } from '../services/api';
import { StatusChip } from '../components/common/DataTable';
import { toast } from 'react-toastify';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    productsAPI.getById(id)
      .then(r => setProduct(r.data.data))
      .catch(() => toast.error('Failed to load product'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (!product) return <Typography>Product not found</Typography>;

  const images = (product.images || []).filter(i => i && i.url);
  const attrs = (product.attributes || []).filter(a => a && a.name);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/products')}>Back</Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={700}>{product.brand_name} {product.name}</Typography>
          <Typography variant="body2" color="text.secondary">SKU: {product.sku}</Typography>
        </Box>
        <StatusChip status={product.status} />
        <Button variant="outlined" startIcon={<Edit />} onClick={() => navigate('/products', { state: { editProduct: product } })}>Edit</Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card sx={{ mb: 2 }}>
            <Box sx={{ position: 'relative', bgcolor: '#f5f5f5', borderRadius: 2, overflow: 'hidden', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {images.length > 0 ? (
                <img src={images[selectedImage]?.url} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <DirectionsCar sx={{ fontSize: 120, color: 'text.disabled' }} />
              )}
            </Box>
            {images.length > 1 && (
              <Box sx={{ display: 'flex', gap: 1, p: 1, overflowX: 'auto' }}>
                {images.map((img, i) => (
                  <Box key={i} onClick={() => setSelectedImage(i)} sx={{ width: 72, height: 52, borderRadius: 1, overflow: 'hidden', cursor: 'pointer', border: selectedImage === i ? '2px solid #1a237e' : '2px solid transparent', flexShrink: 0 }}>
                    <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                ))}
              </Box>
            )}
          </Card>
          {product.description && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>Description</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>{product.description}</Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h4" color="primary.main" fontWeight={800} gutterBottom>{fmt(product.selling_price)}</Typography>
              <Chip label={product.condition} color="success" />
              <Divider sx={{ my: 2 }} />
              <Table size="small">
                <TableBody>
                  {[
                    ['Brand', product.brand_name],
                    ['Model', product.model],
                    ['Year', product.year],
                    ['Engine', product.engine],
                    ['Fuel Type', product.fuel_type],
                    ['Transmission', product.transmission],
                    ['Doors', product.doors],
                    ['Seats', product.seats],
                    ['Stock', product.total_stock],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <TableRow key={label}>
                      <TableCell sx={{ fontWeight: 600, border: 0, py: 0.8, color: 'text.secondary' }}>{label}</TableCell>
                      <TableCell sx={{ border: 0, py: 0.8 }}>{value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {attrs.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>Specifications</Typography>
                <Table size="small">
                  <TableBody>
                    {attrs.map((a, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ fontWeight: 600, border: 0, py: 0.8, color: 'text.secondary' }}>{a.name}</TableCell>
                        <TableCell sx={{ border: 0, py: 0.8 }}>{a.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductDetailPage;
